import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { GenderReplacePipe } from '../../../../../core/pipes/gender-replace.pipe';
import { CommonService } from '../../../../../shared/services/common.service';
import pdfMake from 'pdfmake/build/pdfmake';
import htmlToPdfmake from 'html-to-pdfmake';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { BehaviorSpecificQuestion, PrintFeedBack } from '../feedback';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-print-feedback',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './print-feedback.component.html',
})
export class PrintFeedbackComponent implements OnDestroy {
  printData: PrintFeedBack | null = null;
  behaviorSpecificSections: {
    behaviorId: string;
    questions: BehaviorSpecificQuestion[];
  }[] = [];

  @Input() set feedbackData(val: PrintFeedBack | null) {
    this.initializeBehaviorData(val);
  }

  private async initializeBehaviorData(val: PrintFeedBack | null) {
    this.printData = val;
    this.mapBehaviorSpecificSections();
    this.cd.detectChanges();
    this.initialized = true;
  }

  private mapBehaviorSpecificSections(): void {
    const behaviorSpecificQuestions =
      this.printData?.feedbackDetail?.behaviorSpecificQuestions;

    if (!behaviorSpecificQuestions) {
      this.behaviorSpecificSections = [];
      return;
    }

    if (Array.isArray(behaviorSpecificQuestions)) {
      const groupedQuestions: Record<string, BehaviorSpecificQuestion[]> = {};

      for (const question of behaviorSpecificQuestions) {
        const behaviorId = String(question.caseBehaviorMappingId || 0);
        if (!groupedQuestions[behaviorId]) {
          groupedQuestions[behaviorId] = [];
        }
        groupedQuestions[behaviorId].push(question);
      }

      this.behaviorSpecificSections = Object.keys(groupedQuestions).map(
        behaviorId => ({
          behaviorId,
          questions: groupedQuestions[behaviorId],
        })
      );
      return;
    }

    this.behaviorSpecificSections = Object.keys(behaviorSpecificQuestions).map(
      behaviorId => ({
        behaviorId,
        questions: behaviorSpecificQuestions[behaviorId] || [],
      })
    );
  }

  getBehaviorGraphImages(behaviorId: string): string[] {
    return this.printData?.graphImagesByBehaviorId?.[behaviorId] || [];
  }

  getScoreDisplay(
    score: number | null | undefined,
    display: string | null | undefined
  ): string {
    return score === null || score === undefined ? '-' : display || '-';
  }

  @ViewChild('pdfContent', { static: true }) pdfContent!: ElementRef;
  initialized = false;
  private readonly destroy$ = new Subject<void>();
  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly genderPipe: GenderReplacePipe,
    private readonly commonService: CommonService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getRecommendationBasedOnScore(levelOfSupport: string | undefined) {
    if (!levelOfSupport) {
      return '';
    }
    switch (levelOfSupport) {
      case 'Low':
        return 'Student is showing strong progress. Continue current strategies or consider reducing supports if progress is sustained';
      case 'Moderate':
        return 'Student is making some progress. Continue current supports and adjust as needed.';
      case 'High':
        return 'Student progress is limited. Review the intervention plan and consider intensifying or adding supports.';
      default:
        return '';
    }
  }
  async printPDF() {
    if (this.printData) {
      const pdfContent = document.getElementById('pdf-data');
      if (pdfContent) {
        const pdfMakeContent = htmlToPdfmake(pdfContent.innerHTML);
        const documentDefinition: TDocumentDefinitions = {
          content: pdfMakeContent,
          pageMargins: [20, 20, 20, 20],
          footer: function (currentPage: number) {
            return {
              columns: [
                { text: '', alignment: 'left' },
                {
                  text: `Powered by Insights to Behavior®`,
                  alignment: 'center',
                },
                { text: `${currentPage}`, alignment: 'right' },
              ],
              margin: [15, 0, 10, 0],
              color: '#475467',
            };
          },
          styles: {
            desc: { fontSize: 12 },
            address: { fontSize: 12 },
            title: { fontSize: 12 },
            datalabel: { color: '#475467' },
            dataValue: { color: '#475467' },
          },
          defaultStyle: {
            columnGap: 10,
            fontSize: 10,
          },
        };

        // Generate and download the PDF
        pdfMake
          .createPdf(documentDefinition)
          .download(
            `${this.printData?.feedbackDetail?.studentInfo?.studentFirstName}_${this.printData?.feedbackDetail?.studentInfo?.studentLastName}_Feedback.pdf`
          );

        this.initialized = false;
        this.ngOnDestroy();
      }
    }
  }
}
